import type HighchartsType from 'highcharts/highstock';

import type { ChartAppearance } from '@/features/charts/theme/chartAppearance';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import { getInitialRangeButtonIndex } from './fullScreenChartSeries';
import type { FullscreenRuntimeChart } from './fullScreenChartTypes';

const RANGE_SELECTOR_BUTTONS: HighchartsType.RangeSelectorButtonsOptions[] = [
  { type: 'month', count: 1, text: '1m' },
  { type: 'month', count: 3, text: '3m' },
  { type: 'month', count: 6, text: '6m' },
  { type: 'year', count: 1, text: '1y' },
  { type: 'ytd', text: 'YTD' },
  { type: 'all', text: 'All' }
];

export const createFullScreenRangeSelectorOptions = (
  appearance: ChartAppearance,
  chartSeries: ExtendedSeriesOptionsType[]
): HighchartsType.RangeSelectorOptions => ({
  allButtonsEnabled: true,
  buttonPosition: { align: 'left', x: 0, y: 0 },
  buttons: RANGE_SELECTOR_BUTTONS,
  buttonSpacing: 8,
  buttonTheme: {
    fill: appearance.navigatorBackground,
    height: 22,
    r: 0,
    style: { color: appearance.foreground },
    states: {
      disabled: {
        fill: appearance.hoverBackground,
        style: { color: appearance.disabledForeground }
      },
      hover: {
        fill: appearance.hoverBackground,
        style: { color: appearance.foreground }
      },
      select: {
        fill: appearance.accent,
        style: { color: appearance.accentForeground }
      }
    },
    width: 40
  },
  dropdown: 'never',
  inputBoxHeight: 24,
  inputDateFormat: '%Y-%m-%d',
  inputEditDateFormat: '%Y-%m-%d',
  inputEnabled: false,
  inputStyle: { color: appearance.foreground, fontSize: '12px' },
  labelStyle: { color: appearance.foreground, display: 'none', fontSize: '12px' },
  selected: getInitialRangeButtonIndex(chartSeries)
});

export const applyFullscreenRangeSelectorLayout = (chart: HighchartsType.Chart): void => {
  const rangeSelector = (chart as FullscreenRuntimeChart).rangeSelector;
  if (!rangeSelector) return;
  rangeSelector.zoomText?.hide();
  rangeSelector.positionButtons?.();
};

export const restoreFullscreenRangeSelection = (
  chart: HighchartsType.Chart,
  selected: number | undefined
): void => {
  if (selected === undefined) return;
  const rangeSelector = (chart as FullscreenRuntimeChart).rangeSelector;
  rangeSelector?.setSelected?.(selected);
  rangeSelector?.buttons?.forEach((button, index) => {
    button.setState(index === selected ? 2 : 0);
  });
};

export const installFullscreenRangeSelectorSync = (chart: HighchartsType.Chart): void => {
  const rangeSelector = (chart as FullscreenRuntimeChart).rangeSelector;
  if (!rangeSelector?.render || rangeSelector.fullscreenRangeSelectorSyncInstalled) return;

  const render = rangeSelector.render.bind(rangeSelector);
  rangeSelector.render = (min, max) => {
    render(min, max);
    applyFullscreenRangeSelectorLayout(chart);
  };
  rangeSelector.fullscreenRangeSelectorSyncInstalled = true;
};
